import { useState, useEffect, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { usePartnerAuth } from '../../context/PartnerAuthContext';
import { usePartnerBooking } from '../../context/PartnerBookingContext';
import { supabase, supabasePartner } from '../../lib/supabase';
import { fetchCruiseTimes, fetchBoardingPoint, fetchCruiseNames } from '../../lib/cruiseDetails';
import { formatCardDate, displayDeck, cruiseLabel } from '../../components/shared/helpers';
import { getTicketPdfBase64 } from '../../utils/ticketPDF';
import { normalizeIndianMobile } from '../../utils/phone';
import ModalShell from '../../components/shared/ModalShell';

const RZP_SCRIPT = 'https://checkout.razorpay.com/v1/checkout.js';
const GENERIC_ERROR = 'Something went wrong starting your payment. Please try again.';
// Shown once payment has gone through but the booking couldn't be finalised —
// never invites a second payment. (Same wording as the customer Payment screen.)
const VERIFY_FAIL_MSG = "We couldn't verify your payment. If any amount was deducted, please contact us with your details and we'll sort it out — do not pay again.";
const BOOK_FAIL_MSG = 'Your payment was received, but we could not finalise your booking automatically. Our team has your payment on record and will confirm your booking or arrange a refund shortly. Please do not pay again.';

// Below this many seconds left on the hold, we refuse to open checkout — there
// is not enough time to finish paying, so we send the partner back to reselect
// (a fresh hold) rather than risk paying against a hold that expires mid-checkout.
const MIN_SECONDS_TO_PAY = 120;

// Backend stores the cruise date as ISO (YYYY-MM-DD); the ticket + template want
// DD-MM-YYYY. (Duplicated from the customer Payment screen so that file stays
// untouched.)
function toDdMmYyyy(iso) {
  if (!iso) return '';
  const [y, m, d] = String(iso).split('-');
  return (y && m && d) ? `${d}-${m}-${y}` : String(iso);
}

// 'HH:MM' 24h → 'H:MM AM/PM'. (Duplicated from the customer Payment screen.)
function to12hr(hhmm) {
  if (!hhmm) return '—';
  const [h, m] = hhmm.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${String(m).padStart(2, '0')} ${period}`;
}

// Booking-hold countdown banner, sourced from the PARTNER booking context (the
// shared HoldTimer reads the customer context, so it can't be reused here).
// Duplicated from the other partner screens to stay partner-isolated.
function PartnerHoldTimer({ holdSeconds }) {
  const mm = String(Math.floor(holdSeconds / 60)).padStart(2, '0');
  const ss = String(holdSeconds % 60).padStart(2, '0');
  const urgent = holdSeconds > 0 && holdSeconds < 300;

  return (
    <div className="flex items-center justify-between gap-4 rounded-lg border border-[#D4A843] bg-[rgba(201,168,76,0.1)] px-5 py-4 mb-4 shrink-0">
      <div className="flex flex-col">
        <span className="font-dm-sans text-[13px] text-white/60">Booking held for</span>
        {holdSeconds > 0 ? (
          <span className={`font-cormorant text-3xl leading-none mt-1 tabular-nums ${urgent ? 'text-[#E5484D]' : 'text-[#C9A84C]'}`}>{mm}:{ss}</span>
        ) : (
          <span className="font-dm-sans text-[13px] font-medium text-[#C9A84C] mt-1.5">Your booking hold has expired</span>
        )}
      </div>
      <span className="font-dm-sans text-sm text-white font-medium text-right max-w-[160px] leading-relaxed">
        Complete the booking before time runs out
      </span>
    </div>
  );
}

// Partner ticket routing: generate the boarding-pass ticket (the same PDF the
// customer flow produces) and send it to the AGENT'S WhatsApp via the deployed
// send-ticket edge function — NOT a guest number (partner bookings don't capture
// guest contact). Fully NON-FATAL and fire-and-forget: the booking is already
// written, so any failure here (missing agent, invalid number, generation, send
// error) is only logged — it must never undo or hide the confirmed booking.
// Mirrors the customer sendTicketWhatsApp bodyValues sourcing exactly.
async function sendTicketToAgent(booking, reference, agentId) {
  try {
    // Resolve the agent's phone from partner_agents (via the authenticated
    // partner client, so RLS scopes it to this partner's own agents).
    let agentPhone = null;
    if (agentId) {
      const { data: agent, error: agentErr } = await supabasePartner
        .from('partner_agents')
        .select('agent_phone')
        .eq('id', agentId)
        .maybeSingle();
      if (agentErr) {
        console.error('Ticket not sent — agent lookup failed:', agentErr.message || agentErr);
        return;
      }
      agentPhone = agent?.agent_phone ?? null;
    }

    const norm = normalizeIndianMobile(agentPhone);
    if (!norm.valid) {
      console.error('Ticket not sent — invalid agent phone:', norm.reason);
      return;
    }

    // Build the PDF and, in parallel, source the five template body values from
    // the SAME lookup the printed ticket uses (cruiseDetails, for the booking's
    // cruise date): boarding point and reporting time (formatted via to12hr).
    const [pdf, boarding, times, names] = await Promise.all([
      getTicketPdfBase64(booking, reference || null),
      fetchBoardingPoint({ date: booking.date }),
      fetchCruiseTimes({ cruiseName: booking.cruise_type, date: booking.date }),
      fetchCruiseNames({ cruiseName: booking.cruise_type }),
    ]);

    const boardingPoint = boarding?.boarding_point || 'Guwahati';
    const reportingTime = to12hr(times?.reporting_time);
    const bodyValues = [
      booking.booking_name || 'Guest', // {{1}} name
      // {{2}} cruise — SHORT display name: WhatsApp template variables keep the
      // short name (display only; the stored booking.cruise_type is the
      // fallback and stays the key everywhere else)
      cruiseLabel(names, 'short') || booking.cruise_type || '—',
      booking.date,                    // {{3}} date (DD-MM-YYYY)
      reportingTime,                   // {{4}} reporting time
      boardingPoint,                   // {{5}} boarding point
    ];

    const { error } = await supabase.functions.invoke('send-ticket', {
      body: {
        pdfBase64: pdf.base64,
        fileName: pdf.filename,
        phoneNumbers: [norm.number], // AGENT phone (partner ticket routing)
        bodyValues,
        bookingId: booking.id,
      },
    });
    if (error) console.error('send-ticket invoke returned an error:', error.message || error);
  } catch (err) {
    // Non-fatal — the booking is confirmed regardless; staff can resend later.
    console.error('Partner ticket generate/send failed (booking still confirmed):', err);
  }
}

export default function PartnerPayment() {
  const { partnerSession, partnerProfile, loadingAuth } = usePartnerAuth();
  const {
    partnerId, agentId, tacRate,
    holdId, holdSeconds,
    selectedDate, passengers, selectedCruise,
    paxDetails, agreedTnC,
    setBookingRef, setHoldId, setHoldExpiresAt, setHoldExpiredNotice,
    fading, go, exitFlow, exitToDashboard,
  } = usePartnerBooking();


  // phase: 'ready' | 'working' | 'verifying' | 'booking' | 'seating_failed' | 'failed'
  const [phase, setPhase] = useState('ready');
  const [errorMsg, setErrorMsg] = useState('');
  const [scriptReady, setScriptReady] = useState(false);
  // Active hold on this step → confirm before releasing + exiting to dashboard.
  const [showExitWarning, setShowExitWarning] = useState(false);

  // Load the Razorpay Checkout script once for this screen.
  useEffect(() => {
    if (window.Razorpay) { setScriptReady(true); return; }
    const existing = document.querySelector(`script[src="${RZP_SCRIPT}"]`);
    if (existing) {
      if (existing.getAttribute('data-loaded')) setScriptReady(true);
      else existing.addEventListener('load', () => setScriptReady(true));
      return;
    }
    const s = document.createElement('script');
    s.src = RZP_SCRIPT;
    s.async = true;
    s.onload = () => { s.setAttribute('data-loaded', 'true'); setScriptReady(true); };
    s.onerror = () => setScriptReady(false);
    document.body.appendChild(s);
  }, []);

  // Display-only DISCOUNTED pricing (the server independently recomputes the
  // authoritative discounted amount from pricing + the partner's DB tac_rate;
  // nothing here is trusted for charging). Mirrors PartnerCruiseSummary.
  const rate = Number(tacRate) > 0 ? Number(tacRate) : 0;
  const publicAdult = selectedCruise?.priceAdult ?? 0;
  const publicChild = selectedCruise?.priceChild ?? 0;
  const priceAdult = rate > 0 ? Math.round(publicAdult * (1 - rate / 100)) : publicAdult;
  const priceChild = rate > 0 ? Math.round(publicChild * (1 - rate / 100)) : publicChild;
  const grandTotal = passengers.adults * priceAdult + passengers.children * priceChild;
  const publicGrandTotal = passengers.adults * publicAdult + passengers.children * publicChild;
  const savings = publicGrandTotal - grandTotal;

  const paxSummary = [
    `${passengers.adults} ${passengers.adults === 1 ? 'Adult' : 'Adults'}`,
    passengers.children > 0 ? `${passengers.children} ${passengers.children === 1 ? 'Child' : 'Children'}` : null,
    passengers.infants > 0 ? `${passengers.infants} ${passengers.infants === 1 ? 'Infant' : 'Infants'}` : null,
  ].filter(Boolean).join(' · ');

  const lowTime = holdSeconds > 0 && holdSeconds < MIN_SECONDS_TO_PAY;

  // Lead guest — used for the Razorpay name prefill and the booking name.
  const leadName = paxDetails[0]?.name ?? null;

  // Verify the signature, then (on success) write the real booking. The whole
  // post-checkout flow lives here so the partner never lands in an ambiguous
  // state. Keyed on partnerId (never customerId); guest contact stays null.
  const finalize = useCallback(async (resp) => {
    setPhase('verifying');
    try {
      const { data: vData, error: vErr } = await supabase.functions.invoke('payment', {
        body: {
          action: 'verify',
          razorpay_order_id: resp.razorpay_order_id,
          razorpay_payment_id: resp.razorpay_payment_id,
          razorpay_signature: resp.razorpay_signature,
        },
      });
      if (vErr || !vData || !vData.verified) {
        setErrorMsg(VERIFY_FAIL_MSG);
        setPhase('failed');
        return;
      }

      // Verified → write the booking (atomic seat claim + booking + guests + ref).
      // partnerId (NOT customerId); guest phone/email are intentionally null for
      // a partner booking — the booking is attributed to the partner + agent.
      setPhase('booking');
      const { data: bData, error: bErr } = await supabase.functions.invoke('payment', {
        body: {
          action: 'book',
          razorpay_order_id: resp.razorpay_order_id,
          partnerId,
          passengers: paxDetails,
          bookingName: leadName,
          guestPhone: null,
          guestEmail: null,
        },
      });

      if (!bErr && bData && bData.booked) {
        // Real reference for the Confirmation screen; stop the hold countdown so
        // a confirmed partner is never bounced back to availability.
        const reference = bData.reference || '';
        setBookingRef(reference);
        setHoldExpiredNotice(false);
        setHoldId(null);
        setHoldExpiresAt(null);

        // Auto-deliver the boarding-pass ticket to the AGENT'S WhatsApp. Build the
        // booking object the generator expects from the book result + partner
        // context. Fire-and-forget: NOT awaited, so a slow/failed send never
        // blocks or breaks the confirmation, and a ticket failure can never undo
        // the booking.
        const isoDate = selectedDate;
        sendTicketToAgent(
          {
            id: bData.bookingId,
            booking_name: leadName,
            cruise_type: selectedCruise.cruiseName,
            deck: selectedCruise.deckLabel,
            date: toDdMmYyyy(isoDate),
            adults: passengers.adults,
            children: passengers.children,
            infants: passengers.infants,
            tour_leaders: 0,
          },
          reference,
          agentId,
        );

        go('/partner/book/confirmation');
        return;
      }

      if (bData && bData.seating_failed) {
        setPhase('seating_failed');
        return;
      }

      setErrorMsg(BOOK_FAIL_MSG);
      setPhase('failed');
    } catch (e) {
      setErrorMsg(BOOK_FAIL_MSG);
      setPhase('failed');
    }
  }, [partnerId, agentId, paxDetails, leadName, selectedDate, selectedCruise, passengers, setBookingRef, setHoldId, setHoldExpiresAt, setHoldExpiredNotice, go]);

  // Start payment: re-check the hold has enough time, create an order on the
  // server (partnerId + agentId — the server verifies approval + charges the
  // discounted amount), then open Razorpay Checkout with the server-returned order.
  const handlePay = async () => {
    if (phase === 'working' || phase === 'verifying' || phase === 'booking') return;
    if (holdSeconds < MIN_SECONDS_TO_PAY) return; // safeguard — handled by the lowTime view
    if (!scriptReady || !window.Razorpay) {
      setErrorMsg('The payment window is still loading. Please try again in a moment.');
      return;
    }

    setErrorMsg('');
    setPhase('working');
    try {
      const { data, error } = await supabase.functions.invoke('payment', {
        // passengers/bookingName are a NON-monetary snapshot the server stores
        // on the payments row, so the Razorpay webhook can complete this
        // booking with the real guest list if the browser never returns from
        // checkout. Guest contact stays null by design — the booking is
        // attributed to the partner + agent, matching the book call below.
        body: {
          action: 'create-order',
          partnerId,
          agentId,
          holdId,
          passengers: paxDetails,
          bookingName: leadName,
          guestPhone: null,
          guestEmail: null,
        },
      });

      if (data && data.error === 'hold_expired') {
        go('/partner/book/availability');
        return;
      }
      if (error || !data || data.error || !data.orderId) {
        setPhase('ready');
        setErrorMsg(GENERIC_ERROR);
        return;
      }

      const rzp = new window.Razorpay({
        key: data.keyId,            // public checkout key, from the server
        amount: data.amount,        // server-computed DISCOUNTED paise
        currency: data.currency,
        order_id: data.orderId,
        name: 'Alfresco Grand',
        description: `${cruiseLabel(selectedCruise, 'short')} · ${displayDeck(selectedCruise.cruiseName, selectedCruise.deckLabel)}`,
        prefill: { name: leadName || '' }, // lead guest name; contact/email left blank
        theme: { color: '#C9A84C' },
        handler: (resp) => { finalize(resp); },
        modal: {
          // Dismissed without paying — keep them here with the hold intact to retry.
          ondismiss: () => { setPhase((p) => (p === 'working' ? 'ready' : p)); },
        },
      });
      rzp.on('payment.failed', () => {
        setPhase('ready');
        setErrorMsg('Your payment could not be completed. Please try again.');
      });
      rzp.open();
    } catch (e) {
      setPhase('ready');
      setErrorMsg(GENERIC_ERROR);
    }
  };

  // Guards — wait for the auth check, then require an approved partner session,
  // an active hold (with its cruise + date), and accepted terms. A refresh clears
  // in-memory state by design, so fall back to the partner availability screen.
  if (loadingAuth) {
    return (
      <div className="w-full max-w-[480px] p-px rounded-2xl bg-gradient-to-b from-[rgba(212,168,67,0.32)] via-[rgba(255,255,255,0.06)] to-[rgba(255,255,255,0.02)] card-enter">
        <div className="bg-[#0D1E35] rounded-[15px] shadow-[0_40px_80px_rgba(0,0,0,0.55)] px-7 py-8">
          <p className="font-dm-sans text-sm text-white/55 font-light leading-relaxed text-center">
            Loading...
          </p>
        </div>
      </div>
    );
  }
  if (!partnerSession) return <Navigate to="/partner/login" replace />;
  if (partnerProfile?.approval_status !== 'approved') return <Navigate to="/partner" replace />;
  if (!selectedCruise || selectedDate === null || !holdId) return <Navigate to="/partner/book/availability" replace />;
  if (!agreedTnC) return <Navigate to="/partner/book/passenger-details" replace />;

  // ── Seating could not be secured after a successful payment ─────────────────
  if (phase === 'seating_failed') {
    return (
      <div className={`w-full max-w-[560px] mx-auto px-0 md:px-2 card-enter transition-opacity duration-[250ms] ${fading ? 'opacity-0' : 'opacity-100'}`}>
        <h1 className="font-cormorant font-normal text-[2rem] md:text-[2.2rem] leading-[1.1] tracking-[-0.01em] text-white mb-4">
          Payment received
        </h1>
        <div className="rounded-xl border border-[rgba(212,168,67,0.4)] bg-[rgba(201,168,76,0.08)] px-5 py-5">
          <p className="font-dm-sans text-sm font-light text-white/75 leading-relaxed mb-3">
            The payment was successful, but we couldn't secure seating for this party. No booking has been made and you will not be charged again.
          </p>
          <p className="font-dm-sans text-sm font-light text-white/75 leading-relaxed">
            Our team will contact you shortly to arrange a full refund. We're sorry for the inconvenience.
          </p>
        </div>
      </div>
    );
  }

  // ── Payment couldn't be verified / booking couldn't be finalised ────────────
  if (phase === 'failed') {
    return (
      <div className={`w-full max-w-[560px] mx-auto px-0 md:px-2 card-enter transition-opacity duration-[250ms] ${fading ? 'opacity-0' : 'opacity-100'}`}>
        <h1 className="font-cormorant font-normal text-[2rem] md:text-[2.2rem] leading-[1.1] tracking-[-0.01em] text-white mb-4">
          We hit a snag
        </h1>
        <div className="rounded-xl border border-[rgba(212,168,67,0.4)] bg-[rgba(201,168,76,0.08)] px-5 py-5">
          <p className="font-dm-sans text-sm font-light text-white/75 leading-relaxed">
            {errorMsg}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`w-full max-w-[600px] mx-auto px-0 md:px-2 card-enter transition-opacity duration-[250ms] ${fading ? 'opacity-0' : 'opacity-100'}`}>

        {/* Header row: back arrow → passenger details · Exit to Dashboard */}
        <div className="flex items-center justify-between mb-4">
          <button
            type="button"
            onClick={() => go('/partner/book/passenger-details')}
            className="flex items-center justify-center w-8 h-8 rounded-full border border-[rgba(212,168,67,0.3)] hover:border-[rgba(212,168,67,0.65)] hover:bg-[rgba(212,168,67,0.06)] transition-[border-color,background-color] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)]"
            aria-label="Go back"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
              <path d="M9 2L4 7L9 12" stroke="#D4A843" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          {/* Active hold → confirm before releasing + exiting */}
          <button
            type="button"
            onClick={() => setShowExitWarning(true)}
            className="font-dm-sans text-[13px] font-medium text-[rgba(212,168,67,0.6)] hover:text-[#C9A84C] hover:underline underline-offset-2 transition-colors duration-200"
          >
            Exit to Dashboard
          </button>
        </div>

        {/* Booking-hold countdown */}
        <PartnerHoldTimer holdSeconds={holdSeconds} />

        <p className="font-dm-sans text-[12px] uppercase tracking-[0.18em] text-[rgba(201,168,76,0.85)] mb-1.5">
          Partner Booking
        </p>
        <h1 className="font-cormorant font-normal text-[2rem] md:text-[2.4rem] leading-[1.1] tracking-[-0.01em] text-white mb-5">
          Payment
        </h1>

        {/* Booking summary — DISCOUNTED partner total (display only) */}
        <div className="rounded-xl border border-white/[0.09] bg-[#162840] p-4 mb-5">
          {[
            ['Date', formatCardDate(selectedDate)],
            ['Cruise', cruiseLabel(selectedCruise)],
            ['Deck', displayDeck(selectedCruise.cruiseName, selectedCruise.deckLabel)],
            ['Time', selectedCruise.time],
            ['Passengers', paxSummary],
          ].map(([label, value], i) => (
            <div key={label} className={`flex items-start justify-between gap-6 py-2.5 ${i > 0 ? 'border-t border-white/[0.06]' : ''}`}>
              <span className="font-dm-sans text-[13px] uppercase tracking-[0.1em] font-medium text-[rgba(212,168,67,0.85)] shrink-0">
                {label}
              </span>
              <span className="font-dm-sans text-sm font-light text-white text-right leading-relaxed min-w-0 break-words">
                {value}
              </span>
            </div>
          ))}
          {rate > 0 && (
            <div className="flex items-center justify-between gap-4 py-2.5 border-t border-white/[0.06]">
              <span className="font-dm-sans text-[13px] uppercase tracking-[0.1em] font-medium text-[rgba(212,168,67,0.85)] shrink-0">
                Partner rate
              </span>
              <span className="font-dm-sans text-[10px] font-bold uppercase tracking-[0.06em] text-[#0A1628] bg-[#C9A84C] rounded-full px-1.5 py-[3px] leading-none">
                &minus;{rate}%
              </span>
            </div>
          )}
          <div className="flex items-center justify-between gap-4 pt-3 mt-1 border-t border-[rgba(212,168,67,0.25)]">
            <span className="font-dm-sans text-sm font-medium text-white">Total <span className="text-white/55 font-light">(GST inclusive)</span></span>
            <span className="font-dm-sans text-lg font-bold text-[#C9A84C] tabular-nums">&#8377;{grandTotal.toLocaleString()}</span>
          </div>
          {rate > 0 && savings > 0 && (
            <p className="font-dm-sans text-[12px] font-light text-white/50 text-right mt-1.5">
              Discount applied: you save &#8377;{savings.toLocaleString()} vs the public rate
            </p>
          )}
        </div>

        {lowTime ? (
          /* Under-2-minute safeguard — not enough time to pay; reselect for a fresh hold */
          <div className="rounded-xl border border-[rgba(212,168,67,0.4)] bg-[rgba(201,168,76,0.08)] px-4 py-4 mb-2">
            <p className="font-dm-sans text-sm font-medium text-white mb-1.5">
              Your hold is about to expire
            </p>
            <p className="font-dm-sans text-sm font-light text-white/65 leading-relaxed mb-4">
              There isn't enough time left to complete payment safely. Please select the cruise again to start a fresh 10-minute hold.
            </p>
            <button
              type="button"
              onClick={exitFlow}
              className="w-full py-[14px] bg-[#C9A84C] text-[#0A1628] font-dm-sans font-semibold text-[12.5px] tracking-[0.15em] uppercase rounded-full active:scale-[0.98] hover:bg-[#B8973E] transition-[transform,background-color] duration-[160ms] ease-[cubic-bezier(0.23,1,0.32,1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(201,168,76,0.5)] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0A1628]"
            >
              Reselect cruise
            </button>
          </div>
        ) : (
          <div>
            {errorMsg && (
              <p className="font-dm-sans text-sm font-light text-white/60 leading-relaxed mb-3 text-center">
                {errorMsg}
              </p>
            )}
            <button
              type="button"
              onClick={handlePay}
              disabled={phase === 'working' || phase === 'verifying' || phase === 'booking'}
              className={`w-full py-[15px] bg-[#C9A84C] text-[#0A1628] font-dm-sans font-semibold text-[12.5px] tracking-[0.15em] uppercase rounded-full transition-[transform,opacity,background-color] duration-[200ms] ease-[cubic-bezier(0.23,1,0.32,1)] focus-visible:outline-none ${
                phase === 'working' || phase === 'verifying' || phase === 'booking'
                  ? 'opacity-60 cursor-not-allowed'
                  : 'opacity-100 active:scale-[0.98] hover:bg-[#B8973E] focus-visible:ring-2 focus-visible:ring-[rgba(201,168,76,0.5)] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0A1628] cursor-pointer'
              }`}
            >
              {phase === 'booking'
                ? 'Confirming the booking...'
                : phase === 'verifying'
                ? 'Verifying payment...'
                : phase === 'working'
                ? 'Opening secure checkout...'
                : `Pay ₹${grandTotal.toLocaleString()}`}
            </button>
            <p className="font-dm-sans text-center text-sm text-white/55 mt-3 leading-relaxed">
              Payments are processed securely by Razorpay
            </p>
            <button
              type="button"
              onClick={exitFlow}
              className="block mx-auto mt-4 mb-2 font-dm-sans text-[13px] font-medium text-[rgba(212,168,67,0.6)] hover:text-[#C9A84C] hover:underline underline-offset-2 transition-colors duration-200"
            >
              Cancel and return to cruise selection
            </button>
          </div>
        )}

        {/* Exit-to-Dashboard confirm — releases the hold and leaves to /partner */}
        {showExitWarning && (
          <ModalShell
            ariaLabel="Leave booking — the seats are on hold"
            onClose={() => setShowExitWarning(false)}
          >
            {/* Ornamental diamond rule */}
            <div className="flex items-center gap-3 mb-4 w-full max-w-[180px]">
              <div className="flex-1 h-px bg-gradient-to-r from-transparent to-[rgba(212,168,67,0.4)]" />
              <div className="w-1.5 h-1.5 rotate-45 bg-[rgba(212,168,67,0.55)] shrink-0" />
              <div className="flex-1 h-px bg-gradient-to-l from-transparent to-[rgba(212,168,67,0.4)]" />
            </div>

            <h2 className="font-cormorant font-normal text-[1.8rem] leading-[1.1] tracking-[-0.01em] text-white mb-3">
              Leave booking?
            </h2>
            <p className="font-dm-sans text-sm font-light text-white/60 leading-relaxed mb-6">
              Your held seats will be released and this booking will not be completed. Leave to the dashboard?
            </p>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={exitToDashboard}
                className="flex-1 py-[13px] bg-transparent border border-white/20 text-white/70 font-dm-sans font-semibold text-[12.5px] tracking-[0.15em] uppercase rounded-full transition-[transform,border-color,color] duration-[160ms] ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.97] hover:border-white/40 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/25 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0D1E35]"
              >
                Leave
              </button>
              <button
                type="button"
                onClick={() => setShowExitWarning(false)}
                className="flex-1 py-[13px] bg-[#C9A84C] text-[#0A1628] font-dm-sans font-semibold text-[12.5px] tracking-[0.15em] uppercase rounded-full transition-[transform,background-color] duration-[160ms] ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.97] hover:bg-[#B8973E] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(201,168,76,0.5)] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0D1E35]"
              >
                Stay
              </button>
            </div>
          </ModalShell>
        )}

      </div>
  );
}
